import { getLocalTimeZone, today, type CalendarDate } from '@internationalized/date';

import type { CalendarDateRange } from '../components/DateRangePicker';
import type { ColumnTable } from 'arquero';

export interface IDailyBalance {
  date: string; // YYYY-MM-DD format
  balance: number;
}

/**
 * Calculate daily balances for a single financial account by working backwards from
 * today's current balance through all transactions.
 *
 * Generates balances from today backwards to either the limitDate (if provided) or
 * the oldest transaction date, whichever comes first.
 *
 * All balances represent end-of-day balances (after all transactions for that day have been applied).
 *
 * @example
 * Scenario: Current balance $1000 on 2024-01-20
 * Transaction 2024-01-15: +$50 debit (spent)
 * Transaction 2024-01-18: +$100 debit (spent)
 *
 * Expected daily balances:
 * 2024-01-14: $1150 (before first transaction)
 * 2024-01-15: $1100 (after spending $50)
 * 2024-01-16: $1100 (no transactions)
 * 2024-01-17: $1100 (no transactions)
 * 2024-01-18: $1000 (after spending $100)
 * 2024-01-19: $1000 (no transactions)
 * 2024-01-20: $1000 (today)
 *
 * Note: Plaid convention is positive = debit (money out), negative = credit (money in)
 *
 * @param transactions - Arquero table containing transaction data for a single account
 * @param currentBalance - Current balance of the account (as of today)
 * @param limitDate - Optional CalendarDate to stop generating balances at (defaults to oldest transaction)
 * @returns Array of daily balance records, or empty array if inputs are invalid
 */
export function calculateDailyBalances(
  transactions: ColumnTable | null,
  currentBalance: number,
  limitDate?: CalendarDate
): IDailyBalance[] {
  const todayDate = today(getLocalTimeZone());

  if (!transactions || transactions.numRows() === 0) {
    return [{ date: todayDate.toString(), balance: currentBalance }];
  }

  // Get all transactions as objects
  const txnObjects = transactions.objects() as Array<{
    id: string;
    date: CalendarDate;
    amount: number;
    merchantId: string | null;
    financialAccountId: string;
    bankConnectionId: string;
  }>;

  // Find the earliest transaction date and go back one more day
  // to show the balance before the first transaction
  const earliestCalendarDate = txnObjects.reduce((min, t) => {
    return t.date.compare(min) < 0 ? t.date : min;
  }, txnObjects[0].date);
  // Subtract one day to show balance before first transaction
  const oldestTransactionDate = earliestCalendarDate.subtract({ days: 1 });

  // Determine the stop date: limitDate or oldest transaction, whichever is earlier
  const stopDate = limitDate
    ? limitDate.compare(oldestTransactionDate) < 0
      ? limitDate
      : oldestTransactionDate
    : oldestTransactionDate;

  // Build a map of date string (YYYY-MM-DD) -> total transaction amount for that day
  const transactionsByDate = new Map<string, number>();
  for (const txn of txnObjects) {
    const dateKey = txn.date.toString();
    const existing = transactionsByDate.get(dateKey) || 0;
    transactionsByDate.set(dateKey, existing + txn.amount);
  }

  // Calculate daily balances working backwards from today to stopDate
  const dailyBalances: IDailyBalance[] = [];
  let runningBalance = currentBalance;

  let currentDate = todayDate;
  while (currentDate.compare(stopDate) >= 0) {
    const dateKey = currentDate.toString();
    const transactionAmount = transactionsByDate.get(dateKey) || 0;

    // Add the balance for this date (balance at end of day, after transactions)
    dailyBalances.push({
      date: currentDate.toString(),
      balance: runningBalance,
    });

    // Work backwards: add transactions to get previous day's balance
    // Plaid: positive = debit (out), negative = credit (in)
    // If we spent +$100 today, yesterday we had $100 more
    // If we received -$100 today, yesterday we had $100 less
    runningBalance += transactionAmount;

    // Move to previous day
    currentDate = currentDate.subtract({ days: 1 });
  }

  // Reverse to get chronological order (earliest to latest)
  dailyBalances.reverse();
  return dailyBalances;
}

export interface ICalculateBalanceBoundariesOptions {
  transactions: ColumnTable | null;
  currentBalance: number;
  dateRange: CalendarDateRange;
}

/**
 * Calculates balance boundaries for a given date range:
 * 1. Calculate daily balances from transactions
 * 2. Extract balance values at start and end of date range
 *
 * Since balances are returned in chronological order (earliest to latest),
 * we can simplify the lookup: if date is out of band, fallback to first/last balance.
 *
 * @param options - Transactions, current balance, and date range
 * @returns Balance values at start/end dates
 */
export function calculateBalanceBoundaries({
  transactions,
  currentBalance,
  dateRange,
}: ICalculateBalanceBoundariesOptions) {
  // Step 1: Calculate daily balances
  const dailyBalances =
    !transactions || currentBalance === undefined ? [] : calculateDailyBalances(transactions, currentBalance);

  // Step 2: Extract balance at start date and end date
  // Since calculateDailyBalances creates one balance per day, use exact match
  const balanceAtStart =
    dailyBalances.length === 0
      ? undefined
      : (() => {
          const startDateString = dateRange.start.toString();
          const exactMatch = dailyBalances.find((b) => b.date === startDateString);
          return exactMatch?.balance ?? dailyBalances[0].balance;
        })();

  const balanceAtEnd =
    dailyBalances.length === 0
      ? undefined
      : (() => {
          const endDateString = dateRange.end.toString();
          const exactMatch = dailyBalances.find((b) => b.date === endDateString);
          return exactMatch?.balance ?? dailyBalances[dailyBalances.length - 1].balance;
        })();

  return {
    balanceAtStart,
    balanceAtEnd,
  };
}
